/**
 * RegistrationSuccess Component
 *
 * Success screen shown after vendor registration is completed.
 * Shows credentials for new users to save.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Center, Group } from '@/components/ui/layout';
import { Text, Button, Badge, Title } from '@/components/ui/core';
import { ActionIcon, CopyButton, Tooltip } from '@/components/ui';
import { Card, Divider } from '@/components/ui/data-display';
import { Alert } from '@/components/ui/feedback';
import {
  IconCheck,
  IconMail,
  IconArrowRight,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconAlertTriangle,
  IconUser,
  IconKey,
} from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';

interface RegistrationSuccessProps {
  businessName: string;
  email: string;
  /** Whether the user is already authenticated */
  isAuthenticated?: boolean;
  /** Display name of the created user */
  displayName?: string | null;
  /** Username (email) for login */
  username?: string | null;
  /** Password used for registration (only for new users) */
  password?: string;
  /** Whether email confirmation is required */
  emailConfirmationRequired?: boolean;
  /** Whether this was an existing user (they need to use their existing password) */
  isExistingUser?: boolean;
}

interface CredentialRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isPassword?: boolean;
}

function CredentialRow({ icon, label, value, isPassword }: CredentialRowProps) {
  const [showPassword, setShowPassword] = useState(false);
  const displayValue = isPassword && !showPassword ? '••••••••••••' : value;

  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        {icon}
        <Stack gap={0}>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text size="sm" fw={500} style={{ fontFamily: isPassword ? 'monospace' : undefined }}>
            {displayValue}
          </Text>
        </Stack>
      </Group>
      <Group gap={4} wrap="nowrap">
        {isPassword && (
          <Tooltip label={showPassword ? 'Hide password' : 'Show password'}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied!' : 'Copy'}>
              <ActionIcon
                variant="subtle"
                color={copied ? 'green' : 'gray'}
                size="sm"
                onClick={copy}
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Group>
  );
}

export function RegistrationSuccess({
  businessName,
  email,
  isAuthenticated = false,
  displayName,
  username,
  password,
  emailConfirmationRequired = true,
  isExistingUser = false,
}: RegistrationSuccessProps) {
  // For new users, show credentials section (not for existing users who need their old password)
  const showCredentials = !isAuthenticated && password && !isExistingUser;

  return (
    <FadeIn delay={200}>
      <Stack gap="xl" align="center">
        {/* Success Icon */}
        <Center>
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconCheck size={48} color="white" />
          </div>
        </Center>

        {/* Status Badge */}
        <Badge
          size="lg"
          variant="gradient"
          gradient={{ from: 'green', to: 'teal' }}
          leftSection={<IconCheck size={14} />}
        >
          Registration Complete
        </Badge>

        {/* Title */}
        <Title order={2} ta="center">
          Welcome{displayName ? `, ${displayName}` : ''}!
        </Title>

        {/* Description */}
        <Text c="dimmed" size="md" ta="center" maw={400}>
          Your vendor account for <strong>{businessName}</strong> has been created successfully.
          {emailConfirmationRequired && ' A confirmation email will be sent to your email address.'}
        </Text>

        {/* Existing User Alert - show when user already had an account */}
        {isExistingUser && !isAuthenticated && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="blue"
            alertVariant="light"
            title="Existing Account"
          >
            An account with this email already exists. Please use your existing password to sign in.
          </Alert>
        )}

        {/* Credentials Card - Only for new users */}
        {showCredentials && (
          <Card withBorder p="lg" radius="md" w="100%" maw={450}>
            <Stack gap="md">
              <Group gap="xs">
                <IconKey size={20} color="var(--mantine-color-indigo-6)" />
                <Text fw={600} size="md">Your Account Details</Text>
              </Group>

              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                alertVariant="light"
              >
                Please save these credentials securely. You will need them to sign in.
              </Alert>

              <Divider />

              <Stack gap="sm">
                {displayName && (
                  <CredentialRow
                    icon={<IconUser size={18} color="var(--mantine-color-gray-6)" />}
                    label="Display Name"
                    value={displayName}
                  />
                )}
                <CredentialRow
                  icon={<IconMail size={18} color="var(--mantine-color-gray-6)" />}
                  label="Username (Email)"
                  value={username || email}
                />
                <CredentialRow
                  icon={<IconKey size={18} color="var(--mantine-color-gray-6)" />}
                  label="Password"
                  value={password}
                  isPassword
                />
              </Stack>
            </Stack>
          </Card>
        )}

        {/* What happens next card */}
        <Card withBorder p="lg" radius="md" w="100%" maw={450}>
          <Stack gap="md">
            <Text fw={600} size="md">What happens next?</Text>

            <Stack gap="sm">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--mantine-color-green-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconCheck size={14} color="white" />
                </div>
                <Text size="sm">
                  {isAuthenticated
                    ? 'Your vendor profile has been created'
                    : 'Your account and vendor profile have been created'}
                </Text>
              </div>

              {emailConfirmationRequired && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'var(--mantine-color-blue-6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconMail size={14} color="white" />
                  </div>
                  <Stack gap={2}>
                    <Text size="sm">You will receive a confirmation email at:</Text>
                    <Text size="sm" fw={500} c="blue">{email}</Text>
                  </Stack>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--mantine-color-violet-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconArrowRight size={14} color="white" />
                </div>
                <Text size="sm">
                  {isAuthenticated
                    ? 'You can now access your vendor dashboard and complete your profile'
                    : 'Sign in with your credentials to access your vendor dashboard'}
                </Text>
              </div>
            </Stack>
          </Stack>
        </Card>

        {/* Action Button */}
        <Button
          component={Link}
          to={isAuthenticated ? '/' : '/auth/login'}
          size="lg"
          radius="md"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
          rightSection={<IconArrowRight size={18} />}
        >
          {isAuthenticated ? 'Go to Dashboard' : 'Sign In to Continue'}
        </Button>

        <Text size="sm" c="dimmed" ta="center">
          {isAuthenticated
            ? 'Your vendor profile is ready. You can start setting up your business.'
            : 'Use the credentials above to sign in to your vendor dashboard.'}
        </Text>
      </Stack>
    </FadeIn>
  );
}
